import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isVentaPresencialEnabled } from '../../Services/API/bff/ventaPresencial/flag';
import { obtenerSucursales, Sucursal } from '../../Services/API/bff/ventaPresencial/client';
import { obtenerParametros } from '../../Services/API/bff/auto';
import { useCotizacionStore } from '../../Services/Providers/CotizacionStore';
import { useEmisionStore } from '../../Services/Providers/EmisionStore';
import { useConfiguracionEmpresa } from '../../Services/Providers/useConfiguracionEmpresa';
import { configEmpresaDeParametros } from '../../Services/Providers/configEmpresaDeParametros';
import { DatosVendedor, guardarDatosVendedor, leerDatosVendedor, normalizarNombre } from '../../Services/ventaPresencial/datosVendedor';
import { esLandingDeVendedor, redirigirComoRutaDesconocida } from '../../Services/ventaPresencial/rutaVendedor';
import useSEOConfig from '../../hooks/seo/useSEOConfig';
import { Cotizador as CotizadorAuto } from '../Auto/Cotizador';
import { ErrorDeCarga } from './ErrorDeCarga';
import { PantallaVendedor } from './PantallaVendedor';

type Estado = 'cargando' | 'error' | 'vendedor' | 'cotizando';

/** Estado de navegación que pone "Nueva cotización" en gracias: empezar de cero. */
export interface EstadoRutaVendedor {
  nuevaCotizacion?: boolean;
}

/**
 * `/auto/vendedor` (US 23446). Con el flag apagado se comporta como cualquier
 * ruta desconocida. Prendido:
 * - Si en esta pestaña hay una cotización del vendedor en curso (se volvió con
 *   "Atrás"), la conserva y muestra el cotizador, igual que el flujo público.
 * - Si no (o si se llega desde "Nueva cotización"), trae los parámetros del
 *   origen con el mismo despachador que el cotizador (monolito o BFF según
 *   `auto_via_bff`), deja una cotización limpia con este `landingUrl`, pinta la
 *   marca del origen y muestra la pantalla del vendedor. Al empezar, fija en la
 *   cotización una foto del vendedor y la sucursal: es lo que se registra en el
 *   BFF, aunque después otro asesor cargue sus datos en el mismo dispositivo.
 */
export const VendedorGate = () => {
  const { state } = useLocation();
  const nuevaCotizacion = (state as EstadoRutaVendedor | null)?.nuevaCotizacion === true;
  const setCotizacionValues = useCotizacionStore((s) => s.setCotizacionValues);
  const resetCotizacionValues = useCotizacionStore((s) => s.resetCotizacionValues);
  const resetEmisionValues = useEmisionStore((s) => s.resetEmisionValues);
  const { setConfig: setConfigEmpresa } = useConfiguracionEmpresa();
  const [estado, setEstado] = useState<Estado>('cargando');
  const [inicial] = useState<DatosVendedor | null>(() => leerDatosVendedor());
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);

  // Uso interno de los asesores: fuera de los buscadores en cualquier origen.
  useSEOConfig({ title: 'Venta presencial', robots: 'noindex, nofollow' });

  useEffect(() => {
    let cancelado = false;
    const cargar = async () => {
      const habilitado = await isVentaPresencialEnabled();
      if (cancelado) return;
      if (!habilitado) {
        redirigirComoRutaDesconocida();
        return;
      }
      const actual = useCotizacionStore.getState().cotizacionValues;
      const enCurso = !nuevaCotizacion && !!actual.ventaPresencial && !!actual.parametrosIniciales && esLandingDeVendedor(actual.landingUrl);
      if (enCurso) {
        setEstado('cotizando');
        return;
      }
      const respuesta: any = await obtenerParametros({ rubro: 'AUTO' });
      if (cancelado) return;
      const parametros = respuesta?.data;
      if (!parametros?.origen) {
        setEstado('error');
        return;
      }
      const [configOrigen, origen] = configEmpresaDeParametros(parametros);
      // Las sucursales las entrega el BFF por origen (se cambian sin subir una versión de la web).
      // Una lista vacía no bloquea: el asesor cotiza igual y el lead queda sin sucursal.
      let lista: Sucursal[];
      try {
        lista = await obtenerSucursales(origen);
      } catch {
        if (!cancelado) setEstado('error');
        return;
      }
      if (cancelado) return;
      resetCotizacionValues();
      resetEmisionValues();
      setCotizacionValues({
        parametrosIniciales: parametros,
        referer: document.referrer,
        landingUrl: window.location.href.split('#')[0],
      });
      setConfigEmpresa(configOrigen, origen);
      setSucursales(lista);
      setEstado('vendedor');
    };
    cargar();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const empezar = (datos: DatosVendedor) => {
    guardarDatosVendedor(datos);
    setCotizacionValues({
      ...useCotizacionStore.getState().cotizacionValues,
      ventaPresencial: { vendedor: normalizarNombre(datos.nombre), sucursal: datos.sucursal },
    });
    setEstado('cotizando');
  };

  if (estado === 'error') return <ErrorDeCarga />;
  if (estado === 'vendedor') return <PantallaVendedor inicial={inicial} sucursales={sucursales} onEmpezar={empezar} />;
  if (estado === 'cotizando') return <CotizadorAuto step={0} subStep={1} desdeVendedor />;
  return (
    <div className="container d-flex justify-content-center align-items-center py-5" style={{ minHeight: '50vh' }}>
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Cargando</span>
      </div>
      <span className="ms-2">Cargando...</span>
    </div>
  );
};
